import { useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { formatPrice, type PersonalizationTag } from "@/lib/personalization";

interface AddonCardProps {
  addon: {
    id: PersonalizationTag;
    name: string;
    shortBenefit: string;
    description: string;
    priceCents: number;
    color: string;
    recommended: boolean;
  };
  isSelected: boolean;
  onToggle: () => void;
  index: number;
}

const AddonCard = ({ addon, isSelected, onToggle, index }: AddonCardProps) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        isVisible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <Card
        className={`border transition-all duration-200 ${
          isSelected
            ? `${addon.color} scale-[1.02]`
            : "border-border scale-100"
        }`}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-display text-lg text-foreground">
                  {addon.name}
                </h3>
                {addon.recommended && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    Recommended for you
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {addon.shortBenefit}
              </p>

              <Collapsible>
                <CollapsibleTrigger className="text-xs text-primary flex items-center gap-1 mt-2 hover:underline">
                  Learn more <ChevronDown size={12} />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 text-xs text-muted-foreground leading-relaxed">
                  {addon.description}
                </CollapsibleContent>
              </Collapsible>
            </div>

            <div className="flex flex-col items-end gap-3 shrink-0">
              <span className="font-display text-xl text-foreground">
                +{formatPrice(addon.priceCents)}
                <span className="text-xs text-muted-foreground font-sans">
                  /mo
                </span>
              </span>
              <Switch
                checked={isSelected}
                onCheckedChange={onToggle}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddonCard;
